import React, { useRef, useState, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';

// Import TinyMCE
import 'tinymce/tinymce';

// Import TinyMCE themes and plugins
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/models/dom';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/visualchars';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/codesample';
import 'tinymce/plugins/pagebreak';
import 'tinymce/plugins/nonbreaking';
import 'tinymce/plugins/save';
import 'tinymce/plugins/directionality';
import 'tinymce/plugins/quickbars';

/**
 * TinyMCE - FREE Self-Hosted Community Edition
 * 
 * License: MIT (completely free when self-hosted!)
 * - No API key required (loads from local files)
 * - No usage limits (unlimited edits, no 1000/month quota)
 * - Works completely offline
 * - All assets bundled locally in /public/tinymce/
 * - Branding removed with config
 * 
 * Note: Cloud version has limits, but we use self-hosted = totally free!
 * Docs: https://www.tiny.cloud/docs/tinymce/latest/
 */

function TinyMCEEditor({ initialContent, onContentChange, title }) {
  const editorRef = useRef(null);
  const [content, setContent] = useState(initialContent || '');

  useEffect(() => {
    setContent(initialContent || '');
  }, [initialContent]);

  const handleImageUpload = (blobInfo, progress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;
      xhr.open('POST', '/api/content/upload-image');

      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (e) => {
        progress(e.loaded / e.total * 100);
      };

      xhr.onload = () => {
        if (xhr.status === 403) {
          reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject('HTTP Error: ' + xhr.status);
          return;
        }

        const json = JSON.parse(xhr.responseText);

        if (!json || typeof json.location !== 'string') {
          reject('Invalid JSON: ' + xhr.responseText);
          return;
        }

        resolve(json.location);
      };

      xhr.onerror = () => {
        reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
      };

      const formData = new FormData();
      formData.append('file', blobInfo.blob(), blobInfo.filename());

      xhr.send(formData);
    });
  };

  const handleEditorChange = (value) => {
    setContent(value);
    if (onContentChange) {
      onContentChange(value);
    }
  };

  return (
    <div style={{ background: 'white' }}>
      <Editor
        onInit={(evt, editor) => editorRef.current = editor}
        value={content}
        init={{
          height: 600,
          menubar: true,
          skin_url: '/tinymce/skins/ui/oxide',
          content_css: '/tinymce/skins/content/default/content.css',
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'visualchars', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'help', 'wordcount',
            'emoticons', 'codesample', 'pagebreak', 'nonbreaking',
            'save', 'directionality', 'quickbars'
          ],
          toolbar: 'undo redo | styles | bold italic underline strikethrough | ' +
            'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist outdent indent | link image media table | ' +
            'removeformat code codesample | emoticons charmap | ' +
            'insertdatetime pagebreak | fullscreen preview | help',
          
          // CRITICAL: Preserve all HTML and inline styles
          extended_valid_elements: '*[*]',  // Allow all elements with all attributes
          valid_children: '+body[style],+body[link]',  // Allow style and link tags in body
          valid_elements: '*[*]',  // Whitelist all elements and attributes
          verify_html: false,  // Don't validate/sanitize HTML
          cleanup: false,  // Don't cleanup/normalize HTML
          convert_urls: false,  // Don't convert URLs
          entity_encoding: 'raw',  // Don't encode entities
          paste_retain_style_properties: 'all',  // Retain all styles on paste
          paste_merge_formats: false,  // Don't merge formats
          
          // Preserve all inline styles
          valid_styles: '*[*]',
          
          // Match preview rendering exactly - CRITICAL for WYSIWYG
          content_style: `
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              font-size: 16px;
              line-height: 1.6;
              padding: 20px;
              margin: 0;
              color: #333;
              max-width: 100%;
            }
            img {
              max-width: 100%;
              height: auto;
              display: block;
              margin: 1.5em auto;
            }
            h1, h2, h3, h4, h5, h6 {
              font-weight: 700;
              line-height: 1.3;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              color: #1a1a1a;
            }
            /* CRITICAL: Preserve all text alignment */
            [style*="text-align: center"],
            [style*="text-align:center"] {
              text-align: center !important;
            }
            [style*="text-align: right"],
            [style*="text-align:right"] {
              text-align: right !important;
            }
            /* Preserve all inline styles */
            * {
              box-sizing: border-box;
            }
          `,
          automatic_uploads: true,
          images_upload_handler: handleImageUpload,
          images_reuse_filename: true,
          file_picker_types: 'image',
          image_title: true,
          image_advtab: true,
          image_caption: true,
          contextmenu: 'link image table',
          quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
          quickbars_insert_toolbar: 'quickimage quicktable',
          relative_urls: false,
          remove_script_host: false,
          convert_urls: true,
          toolbar_mode: 'sliding',
          promotion: false,
          branding: false,
          image_class_list: [
            { title: 'Responsive', value: 'img-responsive' },
            { title: 'Thumbnail', value: 'img-thumbnail' }
          ],
          link_class_list: [
            { title: 'None', value: '' },
            { title: 'External Link', value: 'external-link' }
          ],
          style_formats: [
            { title: 'Headings', items: [
              { title: 'Heading 1', format: 'h1' },
              { title: 'Heading 2', format: 'h2' },
              { title: 'Heading 3', format: 'h3' },
              { title: 'Heading 4', format: 'h4' },
              { title: 'Heading 5', format: 'h5' },
              { title: 'Heading 6', format: 'h6' }
            ]},
            { title: 'Inline', items: [
              { title: 'Bold', format: 'bold' },
              { title: 'Italic', format: 'italic' },
              { title: 'Underline', format: 'underline' },
              { title: 'Strikethrough', format: 'strikethrough' },
              { title: 'Superscript', format: 'superscript' },
              { title: 'Subscript', format: 'subscript' },
              { title: 'Code', format: 'code' }
            ]},
            { title: 'Blocks', items: [
              { title: 'Paragraph', format: 'p' },
              { title: 'Blockquote', format: 'blockquote' },
              { title: 'Div', format: 'div' },
              { title: 'Pre', format: 'pre' }
            ]}
          ]
        }}
        onEditorChange={handleEditorChange}
      />
    </div>
  );
}

export default TinyMCEEditor;
